import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { XsI18nPipe } from '@nx-fe/pipes';
import { FeatureToggleService, hd, IUserInfoModel, ReporterService, UserInfoService } from '@nx-fe/services';
import { Observable, Subject } from 'rxjs';

/** Component  */
@Component({
  selector: 'xs-recipients-list',
  templateUrl: './recipients-list.component.html',
  styleUrls: ['./recipients-list.component.scss'],
})
export class RecipientsListComponent implements OnInit, OnDestroy {
  // private userInfoSearchResource: Observable<IUserInfoModel>;
  private reporterSearchResource: Observable<hd.IReporterModel>;

  /** Array of userInfoIds to be excluded from the recipients auto-fill dropdown */
  @Input()
  public excludeIds: string[] = [];
  /** The user-info-model arary of recipients bound to the component */
  @Input()
  public recipientsList: IUserInfoModel[];
  /** Event-binding for adding a recipient to the email controller */
  @Output()
  public onAddRecipient: EventEmitter<any> = new EventEmitter();
  /** Event-binding for removing a recipient from the email controller */
  @Output()
  public onRemoveRecipient: EventEmitter<any> = new EventEmitter();
  /** Event-binding for setting an error on the email controller */
  @Output()
  public onError: EventEmitter<any> = new EventEmitter();
  /** Recipient type ('to' or 'cc') */
  @Input()
  public recipientType: string;
  /** The localized recipient list's type caption (To: or Cc:) used in the template */
  @Input()
  public recipientTypeCaption: string;
  /** Dictionary of query parameters used when querying the userInfoResource */
  public userInfoQueryParameters = {
    performExternalSearch: 'true',
    includeCachedUsers: 'true',
    take: '5',
    filter: 'email::not',
  };

  /** The localized placeholder for recipient list */
  @Input()
  public recipientPlaceholder: string;

  /** Name of exclude parameter to use in search queries */
  @Input()
  public excludeSearchParameter: string;

  /** Whether the parent email activity is disabled  */
  @Input()
  public isDisabled: boolean;

  // Whether the outboundEmailActivityController is currently resolving any added recipients
  @Input()
  public recipientsBeingResolved: boolean;

  // Whether the search and pick is loading search results
  @Input()
  public searchResultsLoading: boolean;

  /** Dictionary of query parameters used when querying the search-and-pick's bound reporterSearchResource */
  @Input()
  public reporterQueryParameters = {
    take: '5',
    filter: 'email::not',
  };

  /** RegExp for validating email adresses */
  private pattern = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
  /** Whether the input field is focused */
  private _isEditing: boolean;
  /** The recipient flagged for removal upon subsequent press of backspace */
  private recipientFlaggedForRemoval: IUserInfoModel;
  /** Invalid email(s) from previous attempt to add recipient(s), used for error caption */
  private invalidEmailAdresses: string[] = [];
  private unsubscribeWatch: any;
  private clearSearch: string;
  private onComplete: Function;

  constructor(
    private $filter: XsI18nPipe,
    private userInfoService: UserInfoService,
    private reporterService: ReporterService
  ) {
    this.excludeSearchParameter = 'excludePublicIds';
  }

  ngOnInit() {
    if (this.recipientsList && this.recipientsList.length > 0) {
      this.assignToExcludeList(this.recipientsList);
    }

    // this.unsubscribeWatch = this.$scope.$watch(() => this.recipientsList, (newVal: IUserInfoModel[], oldVal: IUserInfoModel[]) => {
    //     if (newVal !== oldVal) {
    //         if (newVal && newVal.length > 0) {
    //             this.assignToExcludeList(newVal);
    //         }
    //     }
    // });
  }

  public userInfoSearchResource = (params: { searchText: string; excludePublicIds?: string }, success: Function) => {
    if (!params.excludePublicIds) {
      delete params.excludePublicIds;
    }
    this.userInfoService.searchUsers(params).subscribe(result => {
      success(result);
    });
  };

  /** Invoked on-pick (selection of a search result) and called from addOrCreateRecipient */
  public addRecipient = (recipient: IUserInfoModel, resolutionCallback?: Function) => {
    if (!recipient) {
      return;
    }
    const userInfoId: string = recipient.userId;
    if (!!userInfoId) {
      this.excludeIds.push(userInfoId);
    }
    this.onAddRecipient.emit({
      recipient: recipient,
      recipientType: this.recipientType,
      resolutionCallback: resolutionCallback,
    });
    this.clearSearch = '';
  };

  public addCustomReporterAsRecipient = (value: string) => {
    const recipient: hd.IReporterModel = {} as hd.IReporterModel;
    recipient.email = value;
    this.addReporterAsRecipient(recipient, this.resolutionCallback);
  };

  /** Invoked on-pick (selection of a search result) */
  public addReporterAsRecipient = (recipient: hd.IReporterModel, resolutionCallback: Function = undefined) => {
    const userInfoId: string = recipient.reporterPublicId;
    const recipientUserModel = {
      userId: userInfoId,
      displayName: recipient.fullName,
      email: recipient.email,
    } as IUserInfoModel;
    if (!!userInfoId) {
      this.excludeIds.push(userInfoId);
    }
    this.onAddRecipient.emit({
      recipient: recipientUserModel,
      recipientType: this.recipientType,
      // Flag to resolve user from email & also invoke callback to push resolved user's userInfoId onto excludeList
      resolutionCallback: this.testRecipient(recipient.email) ? resolutionCallback : undefined,
    });
  };

  /** get the placeholder content when recipient list is empty */
  public getRecipientPlaceholder = (): string => {
    return this.recipientPlaceholder
      ? this.recipientPlaceholder
      : this.$filter.transform('searchAndPick_Empty_Placeholder');
  };

  /** Invoked on-submit (when the enter key is pressed after typing in recipient email(s)) */
  public addOrCreateRecipient = (enteredData: string) => {
    const parsedData = this.parseTypedData(enteredData);
    if (!!parsedData) {
      for (const recipientAdress of parsedData) {
        const recepient = <IUserInfoModel>{ email: recipientAdress };
        this.addRecipient(recepient, this.resolutionCallback);
      }
    }
  };

  /** Callback upon resolution of user */
  public resolutionCallback = (userInfoId: string) => {
    if (userInfoId) {
      this.excludeIds.push(userInfoId.toString());
    }

    if (this.onComplete) {
      this.onComplete();
    }

    // if (!this.$scope.$root.$$phase) {
    //     this.$scope.$digest();
    // }
  };

  /** Removes user from the recipient and exclude lists */
  public removeRecipient = (recipient: IUserInfoModel) => {
    const userInfoId: string = recipient.userId;

    const indexOfRecipientInExcludeList = userInfoId ? this.excludeIds.indexOf(userInfoId.toString()) : -1;
    if (indexOfRecipientInExcludeList > -1) {
      this.excludeIds.splice(indexOfRecipientInExcludeList, 1);
    }
    this.onRemoveRecipient.emit({
      recipient: recipient,
      recipientType: this.recipientType,
    });
    this.clearSearch = '';
  };

  /** Invoked on-blur & on-focus of the input field */
  public toggleEditingState = (focusFlag: boolean) => {
    this._isEditing = focusFlag;
    // On blur, set the current recipientFlaggedForRemoval to null
    if (!focusFlag) {
      this.recipientFlaggedForRemoval = null;
    }
  };

  /** Sets ng-class of container to style according to editing state */
  public getInputState = () => {
    let state = this._isEditing ? 'active' : 'inactive';
    return state;
  };

  /** Handles keypresses */
  public keyPressed = event => {
    // When backspace is pressed
    if (event.keyCode === 8) {
      // If the last recipient has already been flagged for removal, remove it
      if (this.recipientFlaggedForRemoval) {
        this.removeRecipient(this.recipientFlaggedForRemoval);
      }
      // Flag the last recipient in the list for removal
      this.flagLastRecipientForRemoval(event.target.value);
    } else {
      // If it was any other key, unflag the last recipient for removal
      this.recipientFlaggedForRemoval = null;
    }
  };

  /** Flags the last recipient for removal if the input field is empty and there is at least one recipient */
  public flagLastRecipientForRemoval = (searchFilter: string) => {
    if (!searchFilter && this.recipientsList && this.recipientsList.length > 0) {
      this.recipientFlaggedForRemoval = this.recipientsList[this.recipientsList.length - 1];
    } else {
      this.recipientFlaggedForRemoval = null;
    }
  };

  /** Sets ng-class of recipient-elements to style appropriately if they are flagged for removal */
  public isRecipientFlaggedForRemoval = (recipient: IUserInfoModel) => {
    return this.recipientFlaggedForRemoval === recipient;
  };

  /** Adds all valid emails and/or sets error based on all invalid items in the given list */
  public handlePaste = (pastedContent: string) => {
    this.invalidEmailAdresses = [];
    for (const item of this.parsePastedData(pastedContent.trim())) {
      if (this.isRecipientFormat(item)) {
        const parsedEmailAddress = item.substring(item.indexOf('<') + 1, item.lastIndexOf('>'));
        this.addOrCreateRecipient(parsedEmailAddress);
      } else {
        // If the content isn't in the recipient format, it's probably not being copied from an email client, so split on spaces
        for (const subContent of item.split(' ')) {
          this.addOrCreateRecipient(subContent);
        }
      }
    }
  };

  /** Splits content of given string on supported delimiters (',' ';' ':', ' ') */
  public parsePastedData = (pastedContent: string): string[] => {
    return pastedContent.split(/[,;: ]+/);
  };

  /** Splits content of given string on all above mentioned delimiters in addition to spaces */
  public parseTypedData = (typedContent: string) => {
    if (!typedContent.trim()) {
      return;
    } else {
      return typedContent.length > 1 ? typedContent.trim().split(/[,;: ]+/) : typedContent;
    }
  };

  public ngOnDestroy() {
    // this.unsubscribeWatch();
  }

  /** Whether the given string is in the format: "descriptive name <email@service.com>", returns true if the string contains a '<' and a '>' */
  private isRecipientFormat = (recipient: string) => {
    recipient = recipient.trim(); // Remove leading and trailing white spaces & the line terminiating character
    return recipient.indexOf('<') > -1 && recipient.lastIndexOf('>') > 3; // Min length of a valid email is 5 (a@b.c), so index of closing '>' should be atleast >=4
  };

  /** Whether the given string is in valid email format */
  public testRecipient = (recipient: string) => {
    const xpattern = new RegExp(this.pattern, 'gm');
    return xpattern.test(recipient);
  };

  /** Constructs then sets error on parent email activity */
  private setError = () => {
    const invalidRecipients = this.constructInvalidRecipientsList();
    if (invalidRecipients) {
      const error = this.$filter.transform('email_Recipients_FormatError', invalidRecipients);
      this.onError.emit({ error });
    } else {
      this.onError.emit('');
    }
  };

  /** Returns a CSV string containing invalid emails from the previous attempt to add recipient(s) */
  private constructInvalidRecipientsList = (): string => {
    let invalidEmails = '';
    for (const recipient of this.invalidEmailAdresses) {
      if (recipient) {
        invalidEmails += recipient + ', ';
      }
    }
    // Trim the trailing comma/whitespace
    return invalidEmails ? invalidEmails.replace(/(^\s*,)|(,\s*$)/g, '') : '';
  };

  private assignToExcludeList(newUserList: IUserInfoModel[]) {
    this.excludeIds = [];
    newUserList.forEach(element => {
      const userInfoId: string = element.userId;

      if (userInfoId) {
        this.excludeIds.push(userInfoId);
      }
    });
  }

  /** run digest cycle to refresh the bindings*/
  public refresh = () => {
    // if (!this.$scope.$root.$$phase) {
    //     this.$scope.$apply();
    // }
  };

  public isLoading = () => {
    return this.recipientsBeingResolved || this.searchResultsLoading;
  };
}
